import { memo, useMemo } from 'react';
import { TrackType } from '@goldcast/player-config/src/types/Track/Type';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import { SegmentsTrack } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Track/SegmentsTrack';
import { VideoSegment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/VideoSegment';
import { isSplitsInTimelineEnabledPure } from '@goldcast/player-config/src/utils/clipUtils';
import Waveform from './Waveform';
import {
  getSegmentDisplayText,
  getSegmentDisplayTextWithIntroOutroPrefix,
  truncateDisplayText,
  getSegmentWidth
} from '../utils';
import { MIN_SEGMENT_WIDTH } from '../timeUtils';
import { useRemoveVideoSegment } from '../hooks/useRemoveVideoSegment';
import useEditorRestrictions from '@/hooks/useEditorRestrictions';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import IconButton from '@/components/atoms/Button/IconButton';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

export enum AddSegmentType {
  INTRO = 'intro',
  OUTRO = 'outro'
}

interface IntroOutroSegmentProps {
  type: AddSegmentType;
}

function IntroOutroSegment({ type }: IntroOutroSegmentProps) {
  const { clipData, clipId } = useClipsContext();
  const { isBlocked } = useEditorRestrictions();
  const isSegmentEditBlocked = isBlocked('timeline-intro-outro-edit');
  const isSplitsEnabled = useMemo(() => {
    return isSplitsInTimelineEnabledPure({
      clip: clipData,
      isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
    });
  }, [clipData]);

  const playerConfig = clipData.asset_metadata.player_config;
  const removeVideoSegment = useRemoveVideoSegment(clipId, type === AddSegmentType.INTRO ? 'intro' : 'outro');

  const segmentData = useMemo(() => {
    const segmentTracks = playerConfig?.playerConfig.tracks.find(
      track => track.type === TrackType.SEGMENTS
    ) as SegmentsTrack;
    const videoSegments = segmentTracks?.segments.filter(
      segment => segment.type === SegmentType.VIDEO
    ) as VideoSegment[];
    if (type === AddSegmentType.INTRO) {
      return videoSegments?.find(segment => segment.id === 'intro');
    }
    return videoSegments?.find(segment => segment.id === 'outro');
  }, [playerConfig, type]);

  if (!segmentData || isSplitsEnabled) {
    return null;
  }

  return (
    <div
      className={type === AddSegmentType.INTRO ? 'pr-[1.5px]' : 'pl-[1.5px]'}
      style={{
        width: `max(calc(${segmentData ? getSegmentWidth(segmentData) : 0}px * var(--timeline-zoom)),${MIN_SEGMENT_WIDTH}px)`
      }}
      data-testid={type === AddSegmentType.INTRO ? 'intro-segment' : 'outro-segment'}
    >
      <div className="group/intro-outro relative flex h-full flex-col gap-1">
        <div className="absolute inset-0 rounded-[7px] border-2 border-slate-200 group-hover/intro-outro:border-slate-800"></div>
        <div className="group/chapter relative mx-[4px] mt-[4px] min-h-[30px] overflow-hidden rounded-[3px] bg-slate-200 hover:bg-slate-500">
          <IconButton
            icon="IconTrash"
            buttonClassName="!rounded-full absolute top-1 right-1 opacity-0 group-hover/intro-outro:opacity-100 !bg-slate-950 w-6 h-6 z-10"
            iconClassName=" text-white"
            variation="checkbox"
            // !! TODO @HiteshChoudhary0098 use correct names here and why no pointer change
            trackingId={type === AddSegmentType.INTRO ? 'remove-intro' : 'remove-outro'}
            disabled={isSegmentEditBlocked}
            onClick={e => {
              e.stopPropagation();
              removeVideoSegment();
            }}
          />
          <video
            height={24}
            className="absolute left-[2px] top-[3px] h-[24px] rounded-sm object-cover"
            src={segmentData?.video?.details.src}
            controls={false}
            autoPlay={false}
            muted={true}
            preload="metadata"
          />
        </div>
        <div className="relative m-[4px] flex h-full flex-col overflow-hidden rounded-[3px] bg-slate-100 px-[2px] py-px">
          <div
            className="truncate text-xs font-medium text-slate-500"
            title={getSegmentDisplayTextWithIntroOutroPrefix(segmentData)}
          >
            {truncateDisplayText(getSegmentDisplayText(segmentData))}
          </div>
          <div className="flex h-full items-center justify-center text-slate-300">
            <Waveform />
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(IntroOutroSegment);
